import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Avatar from '@mui/material/Avatar';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { toast } from 'react-hot-toast';

import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { authErrorMessage } from '../../../utils/authErrors';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../../../config/firebase';

/**
 * @param {string} [defaultEmail] pre-fills the address — see LoginForm.
 */
export function RegisterForm({ open, setOpen, defaultEmail = '' }) {
  const [loading, setLoading] = React.useState(false);
  const [passwordError, setPasswordError] = React.useState('');

  const handleClose = () => {
    setOpen(false);
    setPasswordError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setPasswordError('');
    
    const data = new FormData(event.currentTarget);
    const email = data.get('email');
    const password = data.get('password');
    const confirmPassword = data.get('confirmPassword');

    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const userPayload = {
        id: user.uid,
        createdAt: new Date().toISOString(),
        // LOWERCASE. firestore.rules constrains this to
        // ['client', 'service-provider', 'listing-manager'], so the previous
        // "CLIENT" was refused outright — every registration created an Auth
        // account and then failed this write, leaving the traveller
        // authenticated with no profile. Asserted in the provider repo at
        // tests/rules/client-signup.mjs.
        role: "client",
        profileCompleted: false,
        email: email,
        // The provider console reads `serviceProviderId`; `organizationId` was
        // written here and read nowhere, in either app.
        serviceProviderId: ""
      };

      await setDoc(doc(db, "users", user.uid), userPayload);

      // Sent, not enforced — a traveller who cannot receive it should still be
      // able to browse and book. It exists so an unreachable address is
      // visible before a booking confirmation is sent to it. Not awaited into
      // the failure path: the account already exists by this point.
      sendEmailVerification(user).catch((err) =>
        console.error("Could not send the verification email:", err)
      );

      toast.success("Account created successfully!");
      // Reset form on success (the DOM form will reset automatically on unmount since we close the modal)
      handleClose();
    } catch (error) {
      console.error(error);
      toast.error(authErrorMessage(error, "Failed to register"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
            <PersonAddOutlinedIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            Register
          </Typography>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  type="email"
                  defaultValue={defaultEmail}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  inputProps={{ minLength: 6 }}
                  error={!!passwordError}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  required
                  fullWidth
                  name="confirmPassword"
                  label="Confirm Password"
                  type="password"
                  id="confirmPassword"
                  error={!!passwordError}
                  helperText={passwordError}
                />
              </Grid>
            </Grid>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{ mt: 3, mb: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Sign Up'}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
